import { type DateKey, daysBetween, isDateKey, localDateKey, restoreStats } from '@arrow/game-engine';

import { registerPlayer as postPlayer } from '../api/client.ts';
import type { KeyValueStore } from '../storage/kv.ts';
import { storageKeys } from '../storage/persistence.ts';
import { isInternalDevice, platformKeys, platformStore, usageCountsOn } from './config.ts';

/**
 * The anonymous device identity: a random UUID v4 made once and kept in localStorage.
 * No accounts, no PII. Clearing storage makes a new device, which is fine.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** When storage can't hold the id, it lives for this page load only. */
let volatileId: string | null = null;

export function isUuidV4(value: unknown): value is string {
	return typeof value === 'string' && UUID.test(value);
}

/** A random UUID v4 (crypto.randomUUID, else getRandomValues, else Math.random). */
export function randomUuid(): string {
	const c = typeof crypto === 'undefined' ? undefined : crypto;
	if (c?.randomUUID) return c.randomUUID();
	const bytes = new Uint8Array(16);
	if (c?.getRandomValues) c.getRandomValues(bytes);
	else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
	bytes[6] = (bytes[6]! & 0x0f) | 0x40;
	bytes[8] = (bytes[8]! & 0x3f) | 0x80;
	const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * This device's id (`arrow:v1:device`), created on first call. Never throws: if storage
 * is unavailable it's stable for the page load, and a new device next time.
 */
export function getDeviceId(store: KeyValueStore = platformStore): string {
	const stored = store.get(platformKeys.device);
	if (isUuidV4(stored)) return stored;
	if (volatileId && store === platformStore) return volatileId;
	const id = randomUuid();
	if (!store.set(platformKeys.device, id) && store === platformStore) volatileId = id;
	return id;
}

let registered: Promise<unknown> | null = null;

/**
 * `POST /v1/players` once per page load (fire-and-forget; never throws or blocks), with
 * `internal: true` from an internal device. Off with analytics (`?noanalytics`, the dev
 * toggle, unit tests) and when the player has turned off "Count my plays".
 */
export function registerDevice(store: KeyValueStore = platformStore): void {
	if (registered || !usageCountsOn(store)) return;
	registered = postPlayer(getDeviceId(store), isInternalDevice(store)).catch(() => undefined);
}

export interface Visit {
	/** This device opened Sparrow on an earlier visit (or has play history from before this was tracked). */
	isReturning: boolean;
	/** Calendar days from the first visit to today (0 on the first day). */
	daysSinceFirstSeen: number;
	firstSeen: DateKey;
}

/**
 * Notes this visit against the device's first-seen local date (`arrow:v1:first-seen`).
 * A device with history from before first-seen existed gets the date of its earliest
 * recorded play. Call once per page load (`initPlatform` does). Never throws.
 */
export function recordVisit(todayKey: DateKey = localDateKey(new Date()), store: KeyValueStore = platformStore): Visit {
	const stored = store.get(platformKeys.firstSeen);
	if (isDateKey(stored)) {
		return { isReturning: true, daysSinceFirstSeen: Math.max(0, safeDays(stored, todayKey)), firstSeen: stored };
	}
	const earlier = earliestPlay(store, todayKey);
	const firstSeen = earlier ?? todayKey;
	store.set(platformKeys.firstSeen, firstSeen);
	return { isReturning: earlier !== null, daysSinceFirstSeen: Math.max(0, safeDays(firstSeen, todayKey)), firstSeen };
}

function safeDays(a: DateKey, b: DateKey): number {
	try {
		return daysBetween(a, b);
	} catch {
		return 0;
	}
}

/** The local date of the earliest play in saved stats, if any (before today). */
function earliestPlay(store: KeyValueStore, todayKey: DateKey): DateKey | null {
	try {
		const raw = store.get(storageKeys.stats);
		const stats = restoreStats(raw === null ? undefined : JSON.parse(raw));
		let earliest = Number.POSITIVE_INFINITY;
		for (const levels of Object.values(stats.days)) {
			for (const day of Object.values(levels)) if (day && day.startedAt < earliest) earliest = day.startedAt;
		}
		if (!Number.isFinite(earliest)) {
			// No stats, but a saved game (e.g. practice) still means "been here before".
			return store.keys().some((k) => k.startsWith('arrow:v1:game:')) ? todayKey : null;
		}
		const key = localDateKey(new Date(earliest));
		return key < todayKey ? key : todayKey;
	} catch {
		return null;
	}
}

/** Tests only: forget the per-page-load state. */
export function resetIdentityForTests(): void {
	volatileId = null;
	registered = null;
}
